"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Card } from "@/components/flowbite/card";
import IconButton from "@/components/flowbite/icon-button/icon-button";
import { Edit2Icon } from "@/components/icons-lib";
import { SectionTitle } from "@/components/maps/map-panel/map-panel-details-panel/components/section-title";
import { PolygonPreview } from "@/components/maps/polygon-preview";
import { dictionaries } from "@/dictionaries/content";
import { getBoundaryPolygonPreviewColors } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";
import { cn } from "@/shared/helpers/cn.helper";

import type { CustomLayersPreviewBoundaryPanelPayload } from "../_hooks/use-custom-layers-preview-boundary-panel-payload";

const dict = dictionaries.dataPage.customLayersPreviewPage.boundaryPanel;

const PREVIEW_SIZE = 40;

interface MapLevelBoundaryPanelContentProps {
  payload: CustomLayersPreviewBoundaryPanelPayload;
  isSaving: boolean;
  onSaveName: (name: string) => void;
  onSaveNote: (note: string) => void;
}

/** Boundary info for a map-level boundary: Name (+ inline rename) and Note. */
export function MapLevelBoundaryPanelContent({
  payload,
  isSaving,
  onSaveName,
  onSaveNote,
}: MapLevelBoundaryPanelContentProps) {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const skipNextNameCommitRef = useRef(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(payload.name);
  const [noteValue, setNoteValue] = useState(payload.note);

  // Re-sync local fields when another boundary is selected or fresh data lands.
  useEffect(() => {
    setNameValue(payload.name);
    setIsEditingName(false);
  }, [payload.boundaryId, payload.name]);

  useEffect(() => {
    setNoteValue(payload.note);
  }, [payload.boundaryId, payload.note]);

  useEffect(() => {
    if (!isEditingName) {
      return;
    }

    const input = nameInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    input.select();
  }, [isEditingName]);

  const commitName = useCallback(() => {
    if (skipNextNameCommitRef.current) {
      skipNextNameCommitRef.current = false;
      setIsEditingName(false);
      return;
    }

    setIsEditingName(false);

    const trimmed = nameValue.trim();
    if (trimmed === "" || trimmed === payload.name) {
      setNameValue(payload.name);
      return;
    }

    onSaveName(trimmed);
  }, [nameValue, onSaveName, payload.name]);

  const cancelNameEdit = useCallback(() => {
    skipNextNameCommitRef.current = true;
    setNameValue(payload.name);
    setIsEditingName(false);
  }, [payload.name]);

  const commitNote = useCallback(() => {
    if (noteValue.trim() === payload.note.trim()) {
      return;
    }

    onSaveNote(noteValue);
  }, [noteValue, onSaveNote, payload.note]);

  const startNameEdit = useCallback(() => {
    if (isSaving) {
      return;
    }
    setIsEditingName(true);
  }, [isSaving]);

  const hasPreview =
    payload.previewCoordinates != null && payload.previewCoordinates.length > 0;
  const displayName = nameValue || payload.boundaryId;
  const polygonTint = useMemo(
    () =>
      getBoundaryPolygonPreviewColors({
        schoolId: null,
        color: payload.color,
      }),
    [payload.color],
  );

  return (
    <div className="space-y-6 px-3 py-4">
      <div>
        <SectionTitle title={dict.name} />
        <Card className="mt-2 flex w-full min-w-0 items-center gap-1 py-1 pl-3 pr-2">
          {hasPreview && (
            <PolygonPreview
              className="shrink-0"
              coordinates={payload.previewCoordinates!}
              size={PREVIEW_SIZE}
              {...polygonTint}
            />
          )}
          <div
            className="min-w-0 flex-1"
            title={!isEditingName ? displayName : undefined}
          >
            <input
              ref={nameInputRef}
              aria-label={dict.name}
              className={cn(
                "w-full min-w-0 bg-transparent text-sm font-medium leading-5",
                "m-0 border-0 p-0 text-text-primary shadow-none outline-none",
                "placeholder:text-text-tertiary",
                !isEditingName && "cursor-default truncate",
                isEditingName && "cursor-text",
              )}
              disabled={isSaving}
              name="map-level-boundary-name"
              placeholder={dict.name}
              readOnly={!isEditingName}
              type="text"
              value={isEditingName ? nameValue : displayName}
              onBlur={() => {
                if (isEditingName) {
                  commitName();
                }
              }}
              onChange={(event) => setNameValue(event.target.value)}
              onKeyDown={(event) => {
                if (!isEditingName) {
                  return;
                }
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancelNameEdit();
                }
              }}
            />
          </div>
          <IconButton
            aria-label={dict.renameBoundary}
            className="text-[#3F3A3B] hover:text-[#3F3A3B]"
            disabled={isSaving || isEditingName}
            height={18}
            icon={Edit2Icon}
            variant="secondary"
            width={18}
            onClick={startNameEdit}
          />
        </Card>
      </div>

      <div>
        <SectionTitle title={dict.note} />
        <textarea
          className={cn(
            "min-h-[100px] w-full resize-y rounded-xl border p-3",
            "bg-white text-sm text-text-primary",
            "placeholder:text-text-tertiary",
            "focus:outline-none",
            "focus:border-main-600 border-border-2",
            "disabled:bg-gray-50",
          )}
          disabled={isSaving}
          placeholder={dict.notePlaceholder}
          rows={4}
          value={noteValue}
          onBlur={commitNote}
          onChange={(event) => setNoteValue(event.target.value)}
        />
      </div>
    </div>
  );
}
